/**
 * 電話・FAX の1つの欄（#153、docs/62 §5・★2。自社情報の iOS 風の入力の行）。9/19 の3つの欄（PhoneFields）は顧客・見積・請求書に今のまま残す。
 *   - 数字を打つと、区切りの表（contract の phoneGroupsOf・phoneGroupsForArea）で決まった所に「-」を自動で入れる（contract の typePhone）。
 *     決まらないうちは入れない（「042」）。自分で「-」を打っても二重にしない
 *   - 貼り付け「06-6429-4800」「0664294800」「０６（６４２９）４８００」はどれも「06-6429-4800」。全角は半角に
 *   - 日本語入力の変換中は動かさず、確定したときに直す。変換確定の Enter で欄を移らない（guardComposingEnter）
 *   - カーソルは打った数字のうしろに保つ（「-」が入っても飛ばない）。Backspace で「-」の直後を消したら、その前の数字を消す
 * 保存する値は今と同じ形（「06-6429-4800」。打ちかけの末尾の「-」は付けない）。欄そのもの（<input>）だけを描く＝行の名前は親（GroupInputRow）
 */
import { phoneValueOf, typePhone } from "@genbaryoku/contract";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { guardComposingEnter } from "./ui.jsx";

/** 文字の中の n 個目の数字のうしろの位置（その直後の「-」は飛ばす＝カーソルが「-」の前に残らない） */
export function caretAfterDigits(text: string, n: number): number {
  if (n <= 0) return 0;
  let seen = 0;
  for (let i = 0; i < text.length; i++) {
    if (/[0-9]/.test(text[i]!)) seen++;
    if (seen === n) {
      let j = i + 1;
      while (text[j] === "-") j++;
      return j;
    }
  }
  return text.length;
}
const digitsBefore = (s: string, pos: number) => s.slice(0, pos).replace(/[^0-9０-９]/g, "").length;

export function PhoneField({
  id,
  value,
  onChange,
  invalid = false,
  autoComplete = "off",
  className = "giinp",
  format = typePhone,
  toValue = phoneValueOf,
  inputMode = "tel",
  onEnter,
  testId,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
  invalid?: boolean;
  autoComplete?: string;
  className?: string;
  /** 打った文字 → 欄に見せる形（郵便番号は typeZip） */
  format?: (raw: string) => string;
  /** 欄に見せる形 → 親へ渡す値 */
  toValue?: (text: string) => string;
  inputMode?: "tel" | "numeric";
  /** Enter（変換確定でないとき）。郵便番号は「住所検索」と同じ */
  onEnter?: (() => void) | undefined;
  testId?: string | undefined;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const [text, setText] = useState(() => format(value));
  const textRef = useRef(text);
  textRef.current = text;
  const composing = useRef(false);
  /** 描いたあとに置くカーソル */
  const caret = useRef<number | null>(null);

  // 親から値が変わったら（読み込み・保存のあと）見せる形を作り直す。自分で打っている間は値が同じなので動かない
  useEffect(() => {
    if (toValue(textRef.current) !== value) setText(format(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useLayoutEffect(() => {
    const c = caret.current;
    if (c == null) return;
    caret.current = null;
    const el = ref.current;
    if (el && document.activeElement === el) el.setSelectionRange(c, c);
  });

  /** 打った文字（と、そのときのカーソル）から形を作り、カーソルを同じ数字のうしろへ */
  const apply = (raw: string, pos: number) => {
    const next = format(raw);
    caret.current = caretAfterDigits(next, digitsBefore(raw, pos));
    setText(next);
    textRef.current = next;
    const v = toValue(next);
    if (v !== value) onChange(v);
  };

  return (
    <input
      ref={ref}
      id={id}
      placeholder=" "
      className={className}
      type="text"
      inputMode={inputMode}
      autoComplete={autoComplete}
      aria-invalid={invalid || undefined}
      value={text}
      data-testid={testId}
      onKeyDown={(e) => {
        guardComposingEnter(e);
        if (e.nativeEvent.isComposing || e.keyCode === 229) return;
        if (e.key === "Enter" && onEnter) {
          e.preventDefault();
          onEnter();
          return;
        }
        // 「-」の直後で Backspace：その前の数字を消す（「-」だけ消しても、また入るので）
        const el = e.currentTarget;
        const s = el.selectionStart ?? 0;
        if (e.key === "Backspace" && s === el.selectionEnd && s > 0 && text[s - 1] === "-") {
          e.preventDefault();
          let i = s - 1;
          while (i > 0 && text[i - 1] === "-") i--;
          if (i === 0) return;
          apply(text.slice(0, i - 1) + text.slice(s), i - 1);
        }
      }}
      onCompositionStart={() => {
        composing.current = true;
      }}
      onCompositionEnd={(e) => {
        composing.current = false;
        apply(e.currentTarget.value, e.currentTarget.selectionStart ?? e.currentTarget.value.length);
      }}
      onChange={(e) => {
        if (composing.current) setText(e.target.value);
        else apply(e.target.value, e.target.selectionStart ?? e.target.value.length);
      }}
    />
  );
}
